import type { StyleRenderer, RenderParams, RendererContext } from '../../types';
import { registerRenderer } from './base';
import { drawUchiwaBackground, drawUchiwaBorder, cornerRadiusPx } from '../uchiwa-shape';
import { layoutText, type CharPosition } from '../text';
import { UCHIWA_SIZES } from '../../types';
import { seededRandom, hashString } from '../../utils/random';

/**
 * グリッター (glitter) style — sparkling gradient text
 */
const glitterRenderer: StyleRenderer = {
  name: 'glitter',
  label: 'グリッター',
  description: 'Sparkling gradient text',

  render(ctx: CanvasRenderingContext2D, params: RenderParams, _rc: RendererContext): void {
    const { text, backgroundColor, secondaryColor, textColor, canvasWidth, canvasHeight, textOrientation } = params;

    const cr = cornerRadiusPx(
      UCHIWA_SIZES.jumbo.cornerRadius,
      UCHIWA_SIZES.jumbo.printableArea.width,
      canvasWidth,
    );

    drawUchiwaBackground(ctx, { canvasWidth, canvasHeight, cornerRadius: cr, backgroundColor });

    const pad = canvasWidth * 0.08;
    const areaW = canvasWidth - pad * 2;
    const areaH = canvasHeight - pad * 2;
    const cx = canvasWidth / 2;
    const cy = canvasHeight / 2;

    const positions = layoutText(ctx, {
      text,
      orientation: textOrientation,
      fontFamily: 'Noto Sans JP',
      fontWeight: 'bold',
      areaWidth: areaW,
      areaHeight: areaH,
      centerX: cx,
      centerY: cy,
    });

    // Create gradient across entire text area
    const grad = ctx.createLinearGradient(
      cx - areaW / 2, cy - areaH / 2,
      cx + areaW / 2, cy + areaH / 2,
    );
    grad.addColorStop(0, textColor);
    grad.addColorStop(0.35, secondaryColor);
    grad.addColorStop(0.65, textColor);
    grad.addColorStop(1, secondaryColor);

    // Draw each char with gradient
    for (const pos of positions) {
      drawGlitterChar(ctx, pos, grad);
    }

    // Sparkle overlay with seeded random
    const rng = seededRandom(hashString(text));
    drawSparkles(ctx, positions, rng, canvasWidth, canvasHeight);

    drawUchiwaBorder(ctx, { canvasWidth, canvasHeight, cornerRadius: cr, backgroundColor });
  },

  renderThumbnail(ctx: CanvasRenderingContext2D, size: number): void {
    ctx.fillStyle = '#1A1A2E';
    ctx.fillRect(0, 0, size, size);
    const grad = ctx.createLinearGradient(0, 0, size, size);
    grad.addColorStop(0, '#FF6B9D');
    grad.addColorStop(0.5, '#FFD700');
    grad.addColorStop(1, '#FF6B9D');
    ctx.fillStyle = grad;
    ctx.font = `bold ${size * 0.4}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('✦', size / 2, size / 2);
  },
};

function drawGlitterChar(
  ctx: CanvasRenderingContext2D,
  pos: CharPosition,
  gradient: CanvasGradient,
): void {
  const { char, x, y, fontSize, rotate } = pos;
  const font = `bold ${fontSize}px 'Noto Sans JP', sans-serif`;

  ctx.save();
  ctx.translate(x, y);
  if (rotate) ctx.rotate(rotate);

  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Subtle glow
  ctx.save();
  ctx.shadowColor = 'rgba(255,215,0,0.4)';
  ctx.shadowBlur = fontSize * 0.15;
  ctx.fillStyle = gradient;
  ctx.fillText(char, 0, 0);
  ctx.restore();

  // Main fill
  ctx.fillStyle = gradient;
  ctx.fillText(char, 0, 0);

  ctx.restore();
}

function drawSparkles(
  ctx: CanvasRenderingContext2D,
  positions: CharPosition[],
  rng: () => number,
  _canvasWidth: number,
  _canvasHeight: number,
): void {
  if (positions.length === 0) return;

  const fontSize = positions[0].fontSize;
  const sparkCount = Math.max(8, positions.length * 4);

  ctx.save();
  for (let i = 0; i < sparkCount; i++) {
    // Scatter sparkles around character positions
    const basePos = positions[Math.floor(rng() * positions.length)];
    const angle = rng() * Math.PI * 2;
    const dist = fontSize * (0.4 + rng() * 0.5);
    const sx = basePos.x + Math.cos(angle) * dist;
    const sy = basePos.y + Math.sin(angle) * dist;
    const sparkSize = fontSize * (0.02 + rng() * 0.04);

    ctx.globalAlpha = 0.5 + rng() * 0.5;
    ctx.fillStyle = '#FFFFFF';

    // Draw 4-point star
    drawStar(ctx, sx, sy, sparkSize);
  }
  ctx.restore();
}

function drawStar(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  ctx.beginPath();
  // Vertical line
  ctx.moveTo(x, y - size * 2);
  ctx.lineTo(x + size * 0.3, y);
  ctx.lineTo(x, y + size * 2);
  ctx.lineTo(x - size * 0.3, y);
  ctx.closePath();
  ctx.fill();
  // Horizontal line
  ctx.beginPath();
  ctx.moveTo(x - size * 2, y);
  ctx.lineTo(x, y + size * 0.3);
  ctx.lineTo(x + size * 2, y);
  ctx.lineTo(x, y - size * 0.3);
  ctx.closePath();
  ctx.fill();
}

registerRenderer(glitterRenderer);
export default glitterRenderer;
